/**
 * Puts the editor chrome of a scene on the layer Godot draws its gizmos on, which no decal mask
 * meets, so a decal never lands on a gizmo, a selection box or a debug overlay. Chrome is the
 * drawables under an object tagged `isEditorHelper`. The draw hooks show a draw its layers, as a
 * surface's do (`surfaceDrawHooks.ts`).
 */
import { GODOT_EDITOR_GIZMO_LAYERS } from '../../godot/scene/visualLayers.js';
import { isDrawable } from '../drawable.js';
import { isUnderEditorHelper } from '../editorHelpers.js';
import { restoreInstanceLayers, showInstanceLayers } from '../instanceLayers/instanceLayers.js';
import { TrackedObjects } from '../trackedObjects.js';
import { setVisualLayers } from '../visualLayers.js';
/** Whether `object` draws, and it or an ancestor is the root of editor chrome. */
function isChromeDrawable(object) {
    return isDrawable(object) && isUnderEditorHelper(object);
}
/**
 * The chrome on the gizmo layer, whichever holder placed it. A holder that a later Decal mounts
 * replaces another, and a second hook on one drawable would chain a wrapper for each holder.
 */
const placed = new WeakSet();
export class EditorChromeLayers {
    chrome;
    /** The `chrome.version` of the last `place`, so a render with no new chrome walks nothing. */
    placedVersion = -1;
    constructor(scene) {
        this.chrome = new TrackedObjects(scene, isChromeDrawable);
    }
    /** Places the chrome that arrived since the last call. */
    place() {
        const drawables = this.chrome.current();
        if (this.chrome.version === this.placedVersion)
            return;
        this.placedVersion = this.chrome.version;
        for (const drawable of drawables) {
            if (placed.has(drawable))
                continue;
            placed.add(drawable);
            setVisualLayers(drawable, GODOT_EDITOR_GIZMO_LAYERS);
            showLayersWhenDrawn(drawable);
        }
    }
    dispose() {
        this.chrome.dispose();
    }
}
/** Chains the hooks of `drawable`, so its own still run. */
function showLayersWhenDrawn(drawable) {
    const { onBeforeRender, onAfterRender } = drawable;
    drawable.onBeforeRender = function (renderer, ...rest) {
        onBeforeRender.call(this, renderer, ...rest);
        showInstanceLayers(renderer, this);
    };
    drawable.onAfterRender = function (...args) {
        onAfterRender.apply(this, args);
        restoreInstanceLayers();
    };
}
//# sourceMappingURL=editorChromeLayers.js.map